import { canBeProject, compareByDue, projectProgress, wouldCycle } from '@nedelnik/shared';

import type { ProjectProgress, Task } from '@nedelnik/shared';

/**
 * Проекты (3.8).
 *
 * Отдельного типа «эпик» нет: **проект — это обычная задача, у которой
 * появились вложенные** (3.8.1). Значит, «список проектов» — не таблица в
 * базе, а выборка: задачи, на которые кто-то сослался `parent_id`. Так не
 * приходится решать заранее, что окажется проектом, и не приходится убирать
 * тип, когда вложенные кончились.
 *
 * Правила, общие с сервером и будущим iOS (`projectProgress`, `wouldCycle`),
 * берутся из `shared`: второй реализации быть не должно.
 */

/**
 * Кто чей родитель. `wouldCycle` спрашивает именно такую карту.
 *
 * Отсутствующее поле приводится к `null`: в локальной копии `parent_id` может
 * не быть вовсе, если у него нет часов в `field_clocks`.
 */
export function parentMapOf(tasks: readonly Task[]): Map<string, string | null> {
  return new Map(tasks.map((task) => [task.id, task.parent_id ?? null]));
}

/** Задачи, вложенные в проект, — из всех спринтов сразу (3.8.4, 3.8.8). */
export function childrenOf(tasks: readonly Task[], projectId: string): Task[] {
  return tasks.filter((task) => task.parent_id === projectId).sort(compareByDue);
}

export interface ProjectSummary {
  task: Task;
  progress: ProjectProgress;
}

/**
 * Все проекты с прогрессом (3.8.7).
 *
 * Прогресс считает `projectProgress` из `shared` — по разу на проект, то есть
 * полный проход по задачам на каждый. Для личного трекера это десятки задач, и
 * своя карта «родитель → счётчики» стоила бы второй реализации правила ради
 * экономии, которой никто не заметит.
 *
 * Сверху то, где больше осталось: закрытый на 9/10 проект внимания не требует.
 */
export function projectSummaries(tasks: readonly Task[]): ProjectSummary[] {
  const projectIds = new Set<string>();
  for (const task of tasks) {
    // `!= null`: у записи поле может отсутствовать, и `undefined` в наборе
    // идентификаторов сделал бы проектом задачу с пустым `id`.
    if (task.parent_id != null) projectIds.add(task.parent_id);
  }

  const summaries: ProjectSummary[] = [];
  for (const task of tasks) {
    // Проект, у которого не осталось живых вложенных, проектом быть перестаёт:
    // ссылались на него только надгробия, и в списке он был бы пустой строкой.
    if (!projectIds.has(task.id)) continue;
    summaries.push({ task, progress: projectProgress(task.id, tasks) });
  }

  return summaries.sort((a, b) => {
    const left = a.progress.total - a.progress.done;
    const right = b.progress.total - b.progress.done;
    if (left !== right) return right - left;
    return (a.task.title ?? '').localeCompare(b.task.title ?? '');
  });
}

/**
 * Кандидаты в проекты для задачи.
 *
 * Отсекаются: сама задача, её потомки (3.8.3) и уже вложенные задачи —
 * уровней ровно два, проект не бывает вложенным (`canBeProject` в `shared`).
 * Оба правила спрашиваются у общих функций, а не переписываются здесь: иначе
 * подборщик рано или поздно начнёт предлагать то, что действие отвергнет.
 */
export function projectCandidates(tasks: readonly Task[], taskId: string): Task[] {
  const parentOf = parentMapOf(tasks);
  return tasks.filter(
    (task) => task.id !== taskId && canBeProject(task) && !wouldCycle(taskId, task.id, parentOf),
  );
}

/** Проект задачи или ничего: `parent_id` может указывать на надгробие. */
export function projectOf(byId: ReadonlyMap<string, Task>, task: Task): Task | undefined {
  return task.parent_id == null ? undefined : byId.get(task.parent_id);
}
